/**
 * What is on screen after a learner's own account is gone (#373).
 *
 * Content-free for the same reason the whole-installation receipt is: table names
 * and what survives, and nothing about what was in them - no display name, no
 * username. It renders the server's response and nothing cached, because the
 * cache was cleared when the deletion succeeded.
 *
 * Unlike the whole-installation receipt, the server is still running; the only
 * place to go is the sign-in page.
 */

import type { LearnerDeletionResponse } from '@davinci/contracts';
import { useMessages } from '../../i18n/locale.js';
import { defineMessages } from '../../i18n/messages.js';

const messages = defineMessages({
  en: {
    heading: 'Your account was deleted',
    status:
      'You are signed out, and the account and everything you did were removed from the live server.',
    scrubFailedLead: 'The clean-up of the database\u2019s free space did not finish.',
    scrubFailedBody:
      'Your records are gone, but until the server\u2019s next successful clean-up their bytes may still be recoverable from the database file. The server logged why; tell DaVinci’s operator.',
    receiptNotWritten:
      'DaVinci could not save a copy of this receipt on the server. The deletion finished anyway; this page is the only record.',
    deletedAt: (at: string, appVersion: string, schemaVersion: string | number) =>
      `Deleted at ${at} by DaVinci ${appVersion}, under database schema ${schemaVersion}.`,
    emptied: 'Tables that held something of yours, now empty of it',
    stays: 'What stays, by design',
    notReached: 'What this deletion did not reach',
    signInLink: 'Go to the sign-in page',
  },
  he: {
    heading: 'החשבון שלך נמחק',
    status: 'נותקת, והחשבון וכל מה שעשית הוסרו מהשרת החי.',
    scrubFailedLead: 'הניקוי של השטח הפנוי במסד הנתונים לא הסתיים.',
    scrubFailedBody:
      'הרשומות שלך נעלמו, אבל עד הניקוי המוצלח הבא של השרת ייתכן שעדיין אפשר לשחזר את הבייטים שלהן מקובץ מסד הנתונים. השרת רשם מדוע. כדאי לספר למפעיל של ⁨DaVinci⁩.',
    receiptNotWritten:
      'DaVinci לא הצליח לשמור עותק של הקבלה הזו בשרת. המחיקה הסתיימה בכל זאת. העמוד הזה הוא התיעוד היחיד.',
    deletedAt: (at: string, appVersion: string, schemaVersion: string | number) =>
      `נמחק בתאריך ${at} על ידי DaVinci ${appVersion}, לפי סכמת מסד נתונים ${schemaVersion}.`,
    emptied: 'טבלאות שהחזיקו משהו שלך, וכעת הן ריקות ממנו',
    stays: 'מה שנשאר, בכוונה',
    notReached: 'מה שהמחיקה הזו לא הגיעה אליו',
    signInLink: 'מעבר לעמוד ההתחברות',
  },
});

export function MyDeletionReceipt({
  receipt,
}: {
  readonly receipt: LearnerDeletionResponse;
}): React.JSX.Element {
  const t = useMessages(messages);
  return (
    <section className="panel" aria-labelledby="my-receipt-heading">
      <h2 id="my-receipt-heading">{t.heading}</h2>
      <p role="status">{t.status}</p>

      {!receipt.storageScrubbed ? (
        <div className="notice notice-error" role="alert">
          <p>
            <strong>{t.scrubFailedLead}</strong> {t.scrubFailedBody}
          </p>
        </div>
      ) : null}
      {!receipt.receiptWritten ? (
        <div className="notice" role="alert">
          <p>{t.receiptNotWritten}</p>
        </div>
      ) : null}

      <p>{t.deletedAt(receipt.deletedAt, receipt.appVersion, receipt.schemaVersion)}</p>

      {receipt.tablesEmptied.length > 0 ? (
        <>
          <h3>{t.emptied}</h3>
          <ul className="plain-list">
            {receipt.tablesEmptied.map((table) => (
              <li key={table}>
                <code>{table}</code>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <h3>{t.stays}</h3>
      <ul className="plain-list">
        {receipt.retainedByDesign.map((row) => (
          <li key={row.table}>
            <code>{row.table}</code> — {row.reason}
          </li>
        ))}
      </ul>

      <h3>{t.notReached}</h3>
      <ul className="plain-list">
        {receipt.outsideOurControl.map((limit) => (
          <li key={limit}>{limit}</li>
        ))}
      </ul>

      <p>
        <a href="/login">{t.signInLink}</a>
      </p>
    </section>
  );
}
